import { create, type StateCreator } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import {
  getKaraHardsubUrl,
  getKaraLyricsUrl,
  getKaraMediaUrl,
  pickKaraAnilistId,
  pickKaraAnimeName,
  pickKaraKind,
  pickKaraTitle,
  type KaraEntry,
} from '@/lib/karaokes';

/**
 * The miniplayer's track shape. Kept compact — only fields sourced from
 * Karaoke Mugen are stored. No external metadata (anime cover/banner/
 * accent) is mixed in: the player uses kara-driven visuals end-to-end so
 * artwork stays consistent regardless of where the track was queued from.
 */
export interface MiniPlayerTrack {
  // Identity
  id: string;
  kid: string;

  // Display
  title: string;
  kind: string;
  sequence: number | null;
  duration: number | null;
  year: number | null;

  // Media URLs
  /** The URL the `<audio>` element sources from. mp3 or mp4 audio track. */
  link: string;
  filename: string;
  hardsubUrl: string | null;
  lyricsUrl: string | null;

  // Source metadata
  source: string;
  resolution: number | null;
  singers: string[];
  langs: string[];

  // Series / external linking — derived from kara.series / external IDs
  animeName: string;
  animeId: number | null;
}

/**
 * Adapt a raw Karaoke Mugen entry into the shape the miniplayer reads.
 * All fields come from the kara itself — no anime-side joins.
 */
export function karaToMiniPlayerTrack(kara: KaraEntry): MiniPlayerTrack {
  const kind = pickKaraKind(kara);
  return {
    id: kara.kid,
    kid: kara.kid,
    title: pickKaraTitle(kara),
    kind,
    sequence: kara.songorder ?? null,
    duration: typeof kara.duration === 'number' ? kara.duration : null,
    year: typeof kara.year === 'number' ? kara.year : null,
    // Default to the raw media URL — pure music playback. `<audio>`
    // extracts the audio track from any container (mp3/mp4/webm) so
    // both audio karas and video karas work uniformly. Karaoke video
    // is exposed separately via `hardsubUrl` and the playback-mode
    // toggle below — users opt in for the burnt-in lyrics video.
    link: getKaraMediaUrl(kara),
    filename: kara.mediafile,
    hardsubUrl: getKaraHardsubUrl(kara),
    lyricsUrl: getKaraLyricsUrl(kara),
    source: kara.repository || 'kara.moe',
    resolution: null,
    singers: (kara.singers || []).map((s) => s.name).filter(Boolean),
    langs: (kara.langs || []).map((l) => l.short || l.name).filter(Boolean),
    animeName: pickKaraAnimeName(kara),
    animeId: pickKaraAnilistId(kara),
  };
}

export type PlayTrackOptions = {
  queue?: MiniPlayerTrack[];
  startIndex?: number;
};

export interface HistoryEntry extends MiniPlayerTrack {
  playedAt: number;
}

export type LoopMode = 'off' | 'one' | 'all';
export type DrawerTab = 'lyrics' | 'queue' | 'history';
/**
 * `music` — plays `track.link` (raw audio); the lyrics tab renders
 * synced text. Default and what most users want.
 * `karaoke` — when `track.hardsubUrl` is present, the engine plays the
 * hardsubbed mp4 so the burnt-in karaoke lyrics show up in the
 * immersive view. Falls back to `music` for karas without hardsub.
 */
export type PlaybackMode = 'music' | 'karaoke';

/**
 * Action requested before queue modification — surfaces the "Play now /
 * Play next / Add to queue" modal when the user clicks play on a kara
 * while something else is already in flight.
 */
export interface PendingPlayRequest {
  track: MiniPlayerTrack;
  queue?: MiniPlayerTrack[];
  startIndex?: number;
}

const MAX_HISTORY = 48;

function appendHistory(
  history: HistoryEntry[],
  track: MiniPlayerTrack
): HistoryEntry[] {
  const now = Date.now();
  const next: HistoryEntry = { ...track, playedAt: now };
  const withoutDuplicate = history.filter((item) => item.id !== track.id);
  return [next, ...withoutDuplicate].slice(0, MAX_HISTORY);
}

function shuffledIndex(currentIndex: number, length: number): number {
  if (length <= 1) return currentIndex;
  // Don't repeat the current index — pick from the remaining pool.
  let next = Math.floor(Math.random() * (length - 1));
  if (next >= currentIndex) next += 1;
  return next;
}

type MusicPlayerState = {
  // Queue + identity
  queue: MiniPlayerTrack[];
  history: HistoryEntry[];
  currentIndex: number;
  currentTrack: MiniPlayerTrack | null;

  // Visibility
  isVisible: boolean;

  // Playback state mirrored from the audio engine
  isPlaying: boolean;
  isBuffering: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;

  // Modes
  shuffle: boolean;
  loop: LoopMode;
  infinite: boolean;
  playbackMode: PlaybackMode;

  // UI surfaces
  isDrawerOpen: boolean;
  drawerTab: DrawerTab;
  isImmersive: boolean;
  isMini: boolean;
  /**
   * Desktop-only — when false, the sticky bar collapses to a toast in
   * the bottom-right with just artwork + title + play/pause. Click the
   * chevron to expand back to full chrome. Persisted so the user's
   * preference sticks across navigations.
   */
  isPlayerExpanded: boolean;
  pendingPlay: PendingPlayRequest | null;

  // Actions — queue + identity
  playTrack: (track: MiniPlayerTrack, options?: PlayTrackOptions) => void;
  playFromQueue: (index: number) => void;
  enqueueTrack: (track: MiniPlayerTrack) => void;
  enqueueTracks: (tracks: MiniPlayerTrack[]) => void;
  playNextTrack: (track: MiniPlayerTrack) => void;
  moveQueueItem: (from: number, to: number) => void;
  removeQueueItem: (index: number) => void;
  clearQueue: () => void;
  playNext: () => void;
  playPrevious: () => void;
  closePlayer: () => void;

  // Actions — playback state sync (called by engine)
  setPlaying: (playing: boolean) => void;
  setBuffering: (buffering: boolean) => void;
  setCurrentTime: (time: number) => void;
  setDuration: (duration: number) => void;
  setVolume: (volume: number) => void;
  setMuted: (muted: boolean) => void;

  // Actions — modes
  toggleShuffle: () => void;
  cycleLoop: () => void;
  toggleInfinite: () => void;
  togglePlaybackMode: () => void;
  setPlaybackMode: (mode: PlaybackMode) => void;

  // Actions — UI surfaces
  openDrawer: (tab?: DrawerTab) => void;
  closeDrawer: () => void;
  setDrawerTab: (tab: DrawerTab) => void;
  openImmersive: () => void;
  closeImmersive: () => void;
  setMini: (mini: boolean) => void;
  togglePlayerExpanded: () => void;
  setPlayerExpanded: (expanded: boolean) => void;

  // Actions — pending play action modal
  requestPlay: (request: PendingPlayRequest) => void;
  resolvePending: (action: 'now' | 'next' | 'queue') => void;
  dismissPending: () => void;

  // Actions — history
  clearHistory: () => void;
  replaceHistory: (entries: HistoryEntry[]) => void;
};

type StoreSet = Parameters<StateCreator<MusicPlayerState>>[0];
type StoreGet = Parameters<StateCreator<MusicPlayerState>>[1];

function buildPlayTrack(set: StoreSet) {
  return (track: MiniPlayerTrack, options?: PlayTrackOptions): void => {
    const queue =
      options?.queue && options.queue.length > 0 ? options.queue : [track];
    const matchedIndex = queue.findIndex((item) => item.id === track.id);
    const validStart =
      typeof options?.startIndex === 'number' &&
      options.startIndex >= 0 &&
      options.startIndex < queue.length;
    const startIndex = validStart
      ? (options?.startIndex as number)
      : matchedIndex >= 0
        ? matchedIndex
        : 0;

    set((state) => ({
      queue,
      currentIndex: startIndex,
      currentTrack: queue[startIndex] || track,
      isVisible: true,
      isPlaying: true,
      currentTime: 0,
      duration: 0,
      history: appendHistory(state.history, queue[startIndex] || track),
    }));
  };
}

function buildEnqueueTrack(set: StoreSet, get: StoreGet) {
  return (track: MiniPlayerTrack): void => {
    const { queue, currentTrack, currentIndex } = get();
    if (!currentTrack || queue.length === 0) {
      set({
        queue: [track],
        currentIndex: 0,
        currentTrack: track,
        isVisible: true,
        isPlaying: false,
      });
      return;
    }
    if (queue.some((item) => item.id === track.id)) return;
    set({
      queue: [...queue, track],
      currentIndex,
      currentTrack,
      isVisible: true,
    });
  };
}

function buildEnqueueTracks(set: StoreSet, get: StoreGet) {
  return (tracks: MiniPlayerTrack[]): void => {
    if (tracks.length === 0) return;
    const { queue, currentTrack, currentIndex } = get();
    if (!currentTrack || queue.length === 0) {
      set({
        queue: tracks,
        currentIndex: 0,
        currentTrack: tracks[0] || null,
        isVisible: true,
        isPlaying: false,
      });
      return;
    }
    const existingIds = new Set(queue.map((item) => item.id));
    const deduped = tracks.filter((item) => !existingIds.has(item.id));
    if (deduped.length === 0) return;
    set({
      queue: [...queue, ...deduped],
      currentIndex,
      currentTrack,
      isVisible: true,
    });
  };
}

/**
 * Insert a track immediately after the currently-playing one without
 * disturbing the rest of the queue. Used by the "Play next" action.
 */
function buildPlayNextTrack(set: StoreSet, get: StoreGet) {
  return (track: MiniPlayerTrack): void => {
    const { queue, currentIndex, currentTrack } = get();
    if (!currentTrack || queue.length === 0) {
      set({
        queue: [track],
        currentIndex: 0,
        currentTrack: track,
        isVisible: true,
        isPlaying: false,
      });
      return;
    }
    const filtered = queue.filter((item) => item.id !== track.id);
    const offset = filtered.findIndex(
      (item) => item.id === currentTrack.id
    );
    const insertAt = offset >= 0 ? offset + 1 : currentIndex + 1;
    const nextQueue = [
      ...filtered.slice(0, insertAt),
      track,
      ...filtered.slice(insertAt),
    ];
    set({
      queue: nextQueue,
      currentIndex: offset >= 0 ? offset : currentIndex,
      currentTrack,
      isVisible: true,
    });
  };
}

function buildPlayFromQueue(set: StoreSet, get: StoreGet) {
  return (index: number): void => {
    const { queue } = get();
    if (index < 0 || index >= queue.length) return;
    set((state) => ({
      currentIndex: index,
      currentTrack: queue[index] || null,
      isVisible: true,
      isPlaying: true,
      currentTime: 0,
      duration: 0,
      history:
        queue[index] == null
          ? state.history
          : appendHistory(state.history, queue[index]),
    }));
  };
}

function buildMoveQueueItem(set: StoreSet, get: StoreGet) {
  return (from: number, to: number): void => {
    const { queue, currentIndex } = get();
    if (
      from < 0 ||
      to < 0 ||
      from >= queue.length ||
      to >= queue.length ||
      from === to
    ) {
      return;
    }
    const next = [...queue];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    let nextIndex = currentIndex;
    if (currentIndex === from) nextIndex = to;
    else if (from < currentIndex && to >= currentIndex)
      nextIndex = currentIndex - 1;
    else if (from > currentIndex && to <= currentIndex)
      nextIndex = currentIndex + 1;
    set({
      queue: next,
      currentIndex: nextIndex,
      currentTrack: next[nextIndex] || null,
    });
  };
}

function buildRemoveQueueItem(set: StoreSet, get: StoreGet) {
  return (index: number): void => {
    const { queue, currentIndex, isPlaying } = get();
    if (index < 0 || index >= queue.length) return;
    const nextQueue = queue.filter((_, i) => i !== index);
    if (nextQueue.length === 0) {
      set({
        queue: [],
        currentIndex: 0,
        currentTrack: null,
        isVisible: false,
        isPlaying: false,
      });
      return;
    }
    let nextIndex = currentIndex;
    if (index < currentIndex) nextIndex = currentIndex - 1;
    else if (index === currentIndex)
      nextIndex = Math.min(currentIndex, nextQueue.length - 1);
    set({
      queue: nextQueue,
      currentIndex: nextIndex,
      currentTrack: nextQueue[nextIndex] || null,
      isVisible: true,
      isPlaying: index === currentIndex ? true : isPlaying,
    });
  };
}

function advanceNext(state: MusicPlayerState): Partial<MusicPlayerState> {
  const { queue, currentIndex, shuffle, loop } = state;
  if (queue.length === 0) return { isPlaying: false };

  // Loop-one keeps the same track; the engine handles the rewind.
  if (loop === 'one') {
    return {
      currentIndex,
      currentTrack: queue[currentIndex] || null,
      isPlaying: true,
      currentTime: 0,
    };
  }

  let nextIndex: number;
  if (shuffle) {
    nextIndex = shuffledIndex(currentIndex, queue.length);
  } else {
    nextIndex = currentIndex + 1;
    if (nextIndex >= queue.length) {
      if (loop === 'all') {
        nextIndex = 0;
      } else {
        return { isPlaying: false };
      }
    }
  }
  return {
    currentIndex: nextIndex,
    currentTrack: queue[nextIndex] || null,
    isPlaying: true,
    currentTime: 0,
    history:
      queue[nextIndex] == null
        ? state.history
        : appendHistory(state.history, queue[nextIndex]),
  };
}

function advancePrevious(state: MusicPlayerState): Partial<MusicPlayerState> {
  const { queue, currentIndex, shuffle, loop } = state;
  if (queue.length === 0) return { isPlaying: false };
  if (loop === 'one') {
    return {
      currentIndex,
      currentTrack: queue[currentIndex] || null,
      isPlaying: true,
      currentTime: 0,
    };
  }
  let prevIndex: number;
  if (shuffle) {
    prevIndex = shuffledIndex(currentIndex, queue.length);
  } else {
    prevIndex = currentIndex - 1;
    if (prevIndex < 0) {
      prevIndex = loop === 'all' ? queue.length - 1 : 0;
    }
  }
  return {
    currentIndex: prevIndex,
    currentTrack: queue[prevIndex] || null,
    isPlaying: true,
    currentTime: 0,
    history:
      queue[prevIndex] == null
        ? state.history
        : appendHistory(state.history, queue[prevIndex]),
  };
}

function normalizeHistory(entries: HistoryEntry[]): HistoryEntry[] {
  const deduped = entries.reduce<HistoryEntry[]>((acc, entry) => {
    const existing = acc.findIndex((item) => item.id === entry.id);
    if (existing === -1) {
      acc.push(entry);
      return acc;
    }
    if (entry.playedAt > acc[existing].playedAt) {
      const next = [...acc];
      next[existing] = entry;
      return next;
    }
    return acc;
  }, []);
  return deduped
    .sort((a, b) => b.playedAt - a.playedAt)
    .slice(0, MAX_HISTORY);
}

const LOOP_CYCLE: LoopMode[] = ['off', 'all', 'one'];

const createState: StateCreator<MusicPlayerState> = (set, get) => ({
  // Initial — empty queue, hidden
  queue: [],
  history: [],
  currentIndex: 0,
  currentTrack: null,
  isVisible: false,
  isPlaying: false,
  isBuffering: false,
  currentTime: 0,
  duration: 0,
  volume: 1,
  isMuted: false,
  shuffle: false,
  loop: 'off',
  infinite: false,
  playbackMode: 'music',
  isDrawerOpen: false,
  drawerTab: 'lyrics',
  isImmersive: false,
  isMini: false,
  isPlayerExpanded: true,
  pendingPlay: null,

  // Queue / track actions
  playTrack: buildPlayTrack(set),
  playFromQueue: buildPlayFromQueue(set, get),
  enqueueTrack: buildEnqueueTrack(set, get),
  enqueueTracks: buildEnqueueTracks(set, get),
  playNextTrack: buildPlayNextTrack(set, get),
  moveQueueItem: buildMoveQueueItem(set, get),
  removeQueueItem: buildRemoveQueueItem(set, get),
  clearQueue: () =>
    set({
      queue: [],
      currentIndex: 0,
      currentTrack: null,
      isPlaying: false,
      isVisible: false,
      currentTime: 0,
      duration: 0,
    }),

  playNext: () => set((state) => advanceNext(state)),
  playPrevious: () => set((state) => advancePrevious(state)),

  closePlayer: () =>
    set({
      queue: [],
      currentIndex: 0,
      currentTrack: null,
      isVisible: false,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      isDrawerOpen: false,
      isImmersive: false,
      pendingPlay: null,
    }),

  // Engine-driven setters
  setPlaying: (playing) => set({ isPlaying: playing }),
  setBuffering: (buffering) => set({ isBuffering: buffering }),
  setCurrentTime: (time) => set({ currentTime: time }),
  setDuration: (duration) => set({ duration }),
  setVolume: (volume) => set({ volume, isMuted: volume === 0 }),
  setMuted: (muted) => set({ isMuted: muted }),

  // Modes
  toggleShuffle: () => set((state) => ({ shuffle: !state.shuffle })),
  cycleLoop: () =>
    set((state) => ({
      loop: LOOP_CYCLE[(LOOP_CYCLE.indexOf(state.loop) + 1) % LOOP_CYCLE.length],
    })),
  toggleInfinite: () => set((state) => ({ infinite: !state.infinite })),
  togglePlaybackMode: () =>
    set((state) => ({
      playbackMode: state.playbackMode === 'music' ? 'karaoke' : 'music',
    })),
  setPlaybackMode: (mode) => set({ playbackMode: mode }),

  // UI surfaces
  openDrawer: (tab) =>
    set((state) => ({
      isDrawerOpen: true,
      drawerTab: tab ?? state.drawerTab,
      isImmersive: false,
    })),
  closeDrawer: () => set({ isDrawerOpen: false }),
  setDrawerTab: (tab) => set({ drawerTab: tab }),
  openImmersive: () => set({ isImmersive: true, isDrawerOpen: false }),
  closeImmersive: () => set({ isImmersive: false }),
  setMini: (mini) => set({ isMini: mini }),
  togglePlayerExpanded: () =>
    set((state) => ({ isPlayerExpanded: !state.isPlayerExpanded })),
  setPlayerExpanded: (expanded) => set({ isPlayerExpanded: expanded }),

  // Pending play modal
  requestPlay: (request) => set({ pendingPlay: request }),
  resolvePending: (action) => {
    const pending = get().pendingPlay;
    if (!pending) return;
    if (action === 'now') {
      get().playTrack(pending.track, {
        queue: pending.queue,
        startIndex: pending.startIndex,
      });
    } else if (action === 'next') {
      get().playNextTrack(pending.track);
    } else {
      get().enqueueTrack(pending.track);
    }
    set({ pendingPlay: null });
  },
  dismissPending: () => set({ pendingPlay: null }),

  // History
  clearHistory: () => set({ history: [] }),
  replaceHistory: (entries) =>
    set({ history: normalizeHistory(entries) }),
});

export const useMusicPlayerStore = create<MusicPlayerState>()(
  persist(createState, {
    // v3 — split off from v2 because the track shape lost
    // bannerImage/coverImage/accentColor (KM-only metadata now) and the
    // store gained shuffle/loop/history. v2 state would deserialize with
    // missing fields.
    name: 'music-player-v3',
    storage: createJSONStorage(() => localStorage),
    partialize: (state) => ({
      // Persist only data, not UI state. Re-open should land on a
      // closed drawer / no immersive view / paused.
      queue: state.queue,
      history: state.history,
      currentIndex: state.currentIndex,
      currentTrack: state.currentTrack,
      isVisible: state.isVisible,
      volume: state.volume,
      isMuted: state.isMuted,
      shuffle: state.shuffle,
      loop: state.loop,
      infinite: state.infinite,
      playbackMode: state.playbackMode,
      isPlayerExpanded: state.isPlayerExpanded,
    }),
    onRehydrateStorage: () => {
      return (state) => {
        if (!state) return;
        // Defensive shape fill — persisted tracks from older sessions
        // can predate the `singers` / `langs` array fields; reading
        // `.length` on those crashes consumers (e.g. the immersive
        // player's singer line). Coerce to [] on rehydrate so every
        // downstream reader can trust the type.
        const normalize = (t: MiniPlayerTrack): MiniPlayerTrack => ({
          ...t,
          singers: Array.isArray(t.singers) ? t.singers : [],
          langs: Array.isArray(t.langs) ? t.langs : [],
        });
        state.queue = state.queue.map(normalize);
        state.history = state.history.map((entry) => ({
          ...normalize(entry),
          playedAt: entry.playedAt,
        }));
        if (state.currentTrack) {
          state.currentTrack = normalize(state.currentTrack);
        }
        state.setPlaying(false);
        state.setBuffering(false);
        state.setCurrentTime(0);
      };
    },
  })
);
